import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, Shield, Globe, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DashboardLayout } from "@/pages/DashboardPage";
import { useAuth, API } from "@/App";
import axios from "axios";

const SEVERITY_MAP = {
  critical: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400", dot: "bg-red-500" },
  high: { bg: "bg-orange-500/10", border: "border-orange-500/20", text: "text-orange-400", dot: "bg-orange-500" },
  medium: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", dot: "bg-amber-500" },
  low: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", dot: "bg-emerald-500" },
};

const CATEGORY_ICONS = {
  malware: "MW",
  phishing: "PH",
  ransomware: "RW",
  "zero-day": "ZD",
  apt: "APT",
  ddos: "DD",
};

export default function ThreatFeedPage() {
  const { token } = useAuth();
  const [threats, setThreats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchThreats = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const resp = await axios.get(`${API}/threats/feed`, { headers, withCredentials: true });
      setThreats(resp.data.threats || []);
    } catch (err) {
      setError("Failed to load threat feed");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchThreats();
  }, [fetchThreats]);

  return (
    <DashboardLayout title="Threat Intelligence">
      <div className="space-y-6" data-testid="threat-feed-page">
        <p data-testid="threat-provenance-disclosure" className="text-xs text-amber-200 border-l-2 border-amber-500 pl-3">AI-GENERATED / DEMONSTRATION FEED. These summaries and fallback scenarios are not a verified live threat feed and do not power the globe’s operational telemetry.</p>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Global Threat Feed</h2>
            <p className="text-xs text-zinc-500 mt-1">Unverified AI-generated security summaries</p>
          </div>
          <Button
            onClick={fetchThreats}
            variant="outline"
            className="border-zinc-800 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-50 rounded-sm bg-transparent"
            disabled={loading}
            data-testid="refresh-threats-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {loading && (
          <div className="trace-border rounded-sm bg-zinc-900 p-12 flex flex-col items-center justify-center gap-4">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <p className="text-sm text-zinc-400">Fetching threat intelligence...</p>
          </div>
        )}

        {error && <p className="text-sm text-red-400" data-testid="threat-error">{error}</p>}

        {!loading && threats.length > 0 && (
          <div className="space-y-3">
            {threats.map((t, i) => {
              const sev = SEVERITY_MAP[t.severity] || SEVERITY_MAP.low;
              return (
                <div key={i} className="trace-border rounded-sm bg-zinc-900 p-5 hover:scale-[1.005] transition-all duration-200" data-testid={`threat-card-${i}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-sm ${sev.bg} border ${sev.border} flex items-center justify-center shrink-0`}>
                      <span className={`text-[10px] font-bold tracking-wider ${sev.text}`}>{CATEGORY_ICONS[t.category] || "TH"}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-zinc-50 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{t.title}</h3>
                        <span className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-sm ${sev.bg} ${sev.text} border ${sev.border}`}>
                          {t.severity}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{t.description}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500 flex-wrap">
                        <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {t.region}</span>
                        <span className="font-mono">{t.ioc_count} IOCs</span>
                        <span>{t.affected_systems}</span>
                        <span className="font-mono">{t.first_seen}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
